/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { cliAuthAuthorize } from "../funcs/cliAuthAuthorize.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { AuthorizeResponseBody } from "../models/components/authorizeresponsebody.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  CliAuthAuthorizeRequest,
  CliAuthAuthorizeSecurity,
} from "../models/operations/cliauthauthorize.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type CliAuthAuthorizeMutationVariables = {
  request: CliAuthAuthorizeRequest;
  security?: CliAuthAuthorizeSecurity | undefined;
  options?: RequestOptions;
};

export type CliAuthAuthorizeMutationData = AuthorizeResponseBody;

export type CliAuthAuthorizeMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * authorize cliAuth
 *
 * @remarks
 * Mint a short-lived one-time code bound to a PKCE code_challenge, on behalf of the authenticated dashboard user. Resolves the target project (given slug, else the org's default/first project) and records {user, org, project, scopes:[agent,hooks], challenge} against the code with a ~5 minute TTL. Requires membership in the active organization. Refused (403) while impersonating an organization or user, or without membership in the active org.
 */
export function useCliAuthAuthorizeMutation(
  options?: MutationHookOptions<
    CliAuthAuthorizeMutationData,
    CliAuthAuthorizeMutationError,
    CliAuthAuthorizeMutationVariables
  >,
): UseMutationResult<
  CliAuthAuthorizeMutationData,
  CliAuthAuthorizeMutationError,
  CliAuthAuthorizeMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildCliAuthAuthorizeMutation(client, options),
    ...options,
  });
}

export function mutationKeyCliAuthAuthorize(): MutationKey {
  return ["@gram/client", "cliAuth", "authorize"];
}

export function buildCliAuthAuthorizeMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: CliAuthAuthorizeMutationVariables,
  ) => Promise<CliAuthAuthorizeMutationData>;
} {
  return {
    mutationKey: mutationKeyCliAuthAuthorize(),
    mutationFn: function cliAuthAuthorizeMutationFn({
      request,
      security,
      options,
    }): Promise<CliAuthAuthorizeMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(cliAuthAuthorize(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
